/**
 * PostHog product-analytics + session-replay wiring for Novilot.
 *
 * PRIVACY (non-negotiable): our users write unpublished manuscripts. Their prose
 * must never leave the browser via PostHog. Two independent leak channels are
 * closed here:
 *   1. Session replay records the DOM. We mask ALL text (`maskTextSelector: "*"`)
 *      and all inputs, so recordings show layout + interactions but never a
 *      single readable character of manuscript, story bible, titles, or AI output.
 *   2. Autocapture would attach on-page text (button/element labels, which can be
 *      chapter titles or character names) as event properties. We disable it
 *      entirely (`autocapture: false`) and instrument the funnel by hand instead.
 *
 * Event properties passed to `capture()` MUST be counts, IDs, booleans, or enums.
 * Never pass manuscript text, chapter content, character names, or any prose.
 *
 * PostHog coexists with GA4 (see @/lib/gtag). GA4 = acquisition; PostHog =
 * behaviour. Neither replaces the other.
 */
import posthog from "posthog-js";
import { currentPrivacyChoices } from "@/lib/privacyChoices";
import { isLocalAnalyticsHost } from "@/lib/analyticsHost";
import { trackGaEvent } from "@/lib/gtag";
import { billingEventData, type BillingClientEvent } from "@/lib/billingEvents";

export function captureBilling(event: BillingClientEvent | "pricing_viewed" | "credits_upsell_viewed", props: Record<string, unknown> = {}, persist = false): void {
  if (typeof window === "undefined" || isLocalAnalyticsHost(window.location.hostname) || isPostHogExcludedPath(window.location.pathname) || navigator.doNotTrack === "1") return;
  try {
    const data = billingEventData(props);
    capture(event, data);
    trackGaEvent(event, data);
    if (persist && event !== "pricing_viewed") {
      void fetch("/api/track", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: event, data }), keepalive: true,
      }).catch(() => undefined);
    }
  } catch { /* Analytics must never interrupt a purchase. */ }
}

type EventProps = Record<string, string | number | boolean | null | undefined>;

let initialized = false;

/**
 * Lazily initialise the PostHog client (once) and return it, or null when
 * PostHog is not configured / we are on the server. Safe to call from anywhere;
 * the first call wins and later calls are cheap no-ops.
 */
export function ensurePostHog(): typeof posthog | null {
  if (typeof window === "undefined") return null;
  if (!currentPrivacyChoices().analytics) return null;
  if (isLocalAnalyticsHost(window.location.hostname)) return null;
  // Never boot PostHog on internal admin surfaces (session replay would still
  // send the /metrics URL even with capture_pageview disabled).
  if (isPostHogExcludedPath(window.location.pathname)) return null;
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return null;
  if (initialized) return posthog;
  initialized = true;

  posthog.init(key, {
    // Reverse proxy: ingestion goes through our own domain (/ingest) so ad
    // blockers do not silently drop our data. See next.config.ts rewrites.
    api_host: "/ingest",
    ui_host: "https://us.posthog.com",
    // Only create person profiles for users we explicitly identify (by internal
    // id) — never for anonymous landing-page visitors.
    person_profiles: "identified_only",
    // App Router navigates on the client; we fire $pageview by hand on route
    // change (see PostHogPageview). Disable the automatic one.
    capture_pageview: false,
    capture_pageleave: true,
    before_send: event => {
      if (isPostHogExcludedPath(window.location.pathname)) return null;
      if (Object.values(event?.properties ?? {}).some(value => typeof value === "string" && /\/(?:editor-review|author-review|manuscript-request|review-invitation|read)\//.test(value))) return null;
      if (event?.event === "$exception" && isUnactionableException(event.properties)) return null;
      return event;
    },
    // Autocapture attaches element text to events → can leak titles / names.
    // Off. We instrument the funnel explicitly instead.
    autocapture: false,
    respect_dnt: true,
    session_recording: {
      // Mask every input value AND every text node in recordings. This is the
      // whole privacy guarantee: replays keep clicks, mouse, scroll and rage
      // clicks, but no readable prose.
      maskAllInputs: true,
      maskTextSelector: "*",
    },
  });

  return posthog;
}

/**
 * Fire a PostHog event. Client-only; a no-op before init, on the server, or when
 * PostHog is not configured. Props are counts / IDs / booleans / enums ONLY —
 * never user content.
 */
export function capture(event: string, props?: EventProps): void {
  if (typeof window !== "undefined" && isPostHogExcludedPath(window.location.pathname)) return;
  const ph = ensurePostHog();
  if (!ph) return;
  try {
    ph.capture(event, props);
  } catch {
    /* never let analytics break the app */
  }
}

/**
 * Report a caught exception to PostHog error tracking. Use inside error
 * boundaries: catching an error stops the browser's own throw, so exception
 * autocapture never sees it. Client-only; a no-op before init, on the server,
 * or when PostHog is not configured. Props are counts / IDs / booleans / enums
 * ONLY — never user content.
 */
export function captureException(error: unknown, props?: EventProps): void {
  if (typeof window !== "undefined" && isPostHogExcludedPath(window.location.pathname)) return;
  const ph = ensurePostHog();
  if (!ph) return;
  try {
    ph.captureException(error instanceof Error ? error : new Error(String(error)), props);
  } catch {
    /* never let analytics break the app */
  }
}

export const reportException = captureException;

/** Identify the current user by their INTERNAL id only. No email, no name, no PII. */
export function identify(userId: string): void {
  if (!userId) return;
  if (typeof window !== "undefined" && isPostHogExcludedPath(window.location.pathname)) return;
  const ph = ensurePostHog();
  if (!ph) return;
  try {
    ph.identify(userId);
  } catch {
    /* noop */
  }
}

/** Clear the identified user (call on sign-out) so sessions don't bleed together. */
export function resetAnalytics(): void {
  if (typeof window === "undefined" || !initialized) return;
  try {
    posthog.reset();
  } catch {
    /* noop */
  }
}

/**
 * "Script error." is all a browser reveals when a script from another origin
 * fails, and the ResizeObserver notice reports a skipped layout pass, not a
 * failure. Both arrive without a stack, so there is nothing to act on.
 */
const UNACTIONABLE_EXCEPTION = /^(?:Script error\.?|ResizeObserver loop (?:limit exceeded|completed with undelivered notifications\.?))$/;

type ExceptionEntry = { value?: unknown; stacktrace?: { frames?: unknown[] } };

export function isUnactionableException(properties: Record<string, unknown> | undefined): boolean {
  const list = properties?.$exception_list;
  if (!Array.isArray(list) || list.length === 0) return false;
  return list.every((entry: ExceptionEntry) =>
    typeof entry?.value === "string" && UNACTIONABLE_EXCEPTION.test(entry.value) && !entry.stacktrace?.frames?.length
  );
}

/** Internal admin surfaces that must never create PostHog pageviews or pad session replay. */
export function isPostHogExcludedPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return pathname === "/metrics" || pathname.startsWith("/metrics/") || /^\/(?:editor-review|author-review|manuscript-request|review-invitation|read)\//.test(pathname);
}

/**
 * Drop all PostHog capture (events, pageleave, replay) on excluded paths.
 * Does NOT initialize PostHog — a cold load of /metrics must not phone home.
 */
export function silencePostHogForExcludedPath(): void {
  if (typeof window === "undefined") return;
  if (!initialized) return;
  try {
    posthog.opt_out_capturing();
    posthog.stopSessionRecording();
  } catch {
    /* noop */
  }
}

/** Re-enable capture after leaving an excluded path. Never throws. */
export function resumePostHogCapturing(): void {
  if (typeof window === "undefined") return;
  if (isPostHogExcludedPath(window.location.pathname)) return;
  const ph = ensurePostHog();
  if (!ph) return;
  try {
    if (typeof ph.has_opted_out_capturing === "function" && ph.has_opted_out_capturing()) {
      ph.opt_in_capturing();
    }
  } catch {
    /* noop */
  }
}

/** @deprecated Use silencePostHogForExcludedPath — must not init PostHog on /metrics. */
export function stopSessionRecordingIfNeeded(): void {
  silencePostHogForExcludedPath();
}
